import React, { useState, useEffect, useCallback, useMemo } from 'react';
import NumericCheckbox from './NumericCheckbox'; // Make sure to import it
import './PickupModal.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PickupModal = ({ show, onHide, title, onConfirm, stocks = [] }) => {
    const [groupedDescriptions, setGroupedDescriptions] = useState({});
    const [activeGroup, setActiveGroup] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmationMessage, setConfirmationMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { customerId } = useParams();
    const [stocklists, setStocklist] = useState([]);
    const [reportID, setReportID] = useState("");
    const [incidentDate, setIncidentDate] = useState(new Date());
    const [incidentType, setIncidentType] = useState("");
    const [incident, setIncident] = useState("");
    const navigate = useNavigate();
    
    const groupDescriptions = useMemo(() => {
        const groupedItems = {};
        if (stocks) {
            stocks.forEach((item) => {
                const { description, stockGroup, flt1 } = item;
                if (!groupedItems[stockGroup]) {
                    groupedItems[stockGroup] = [{ Description: description, existingQuantity: flt1, pickUpQuantity: 0, stockListId: stocks.stockListId }];
                } else {
                    groupedItems[stockGroup].push({ Description: description, existingQuantity: flt1, pickUpQuantity: 0, stockListId: stocks.stockListId });
                }
            });
        }
        return groupedItems;
    }, [stocks]);

    

    useEffect(() => {
        if (Array.isArray(stocks) && stocks.length > 0) {
            setGroupedDescriptions(groupDescriptions);
        }
    }, [stocks, groupDescriptions]);

    useEffect(() => {
        if (show) {
            setIncidentDate(new Date());
            setIsSubmitting(false);
        }
    }, [show]);

    const handleValueChange = useCallback((group, descriptionIndex, newValue) => {
        const updatedGroupedDescriptions = { ...groupedDescriptions };
        const item = updatedGroupedDescriptions[group][descriptionIndex];

        if (newValue >= 0 && newValue <= item.existingQuantity) {
            updatedGroupedDescriptions[group][descriptionIndex].pickUpQuantity = newValue;
            setGroupedDescriptions(updatedGroupedDescriptions);

            const selectedDescription = updatedGroupedDescriptions[group][descriptionIndex];
            const selectedStock = stocklists.find(stock => stock.description === selectedDescription.Description);
            const selectedItem = {
                Description: selectedDescription.Description,
                stockListId: selectedStock ? selectedStock.stockListId : undefined,
                quantity: newValue
            };

            const index = selectedItems.findIndex(item => item.Description === selectedItem.Description);

            if (index !== -1) {
                const updatedSelectedItems = [...selectedItems];
                updatedSelectedItems[index].quantity = newValue;
                setSelectedItems(updatedSelectedItems);
            } else {
                setSelectedItems(prevItems => [...prevItems, selectedItem]);
            }
        }
    }, [groupedDescriptions, selectedItems]);

    const toggleGroup = useCallback((group) => {
        setActiveGroup(activeGroup === group ? null : group);
    }, [activeGroup]);

    const isFormValid = () => selectedItems.length > 0 && selectedItems.every(item => item.quantity > 0);

    const handleConfirm = () => {
        if (!isFormValid()) {
            setError("Please select at least one item with a quantity greater than 0.");
            return;
        }
        if (!incidentType) {
            setError("Please select an incident type.");
            return;
        }
    
        // Prepare stock data as an array of arrays [stockListId, pickUpQuantity]
        const stockArray = selectedItems.map(item => {
            const selectedStock = stocks.find(stock => stock.description === item.Description);
            return [
                selectedStock ? selectedStock.stockListId : undefined, // StockListId (Backend expects this)
                item.quantity // PickUpQuantity
            ];
        });
    
        // Check if any stockListId is undefined (indicating an error)
        if (stockArray.some(item => item[0] === undefined)) {
            setError("Error: Stock List ID is missing for some selected items.");
            return;
        }
    
        // Build the payload according to the backend structure
        const payload = {
            StockArray: stockArray,  // Expected format in backend
            WebCustID: customerId    // Customer ID (WebCustID)
        };
    
        // Validate incidentDate
        let validIncidentDate = incidentDate instanceof Date && !isNaN(incidentDate)
            ? incidentDate
            : new Date();

        // Add the 3 fields to the payload
        const updatedPayload = {
            ...payload,
            incidentDate: validIncidentDate.toISOString(),
            incidentType,
            incident,
        };
    
        submitPickupData(updatedPayload);
    };
    
    
    const submitPickupData = (payload) => {
        setError(null);
        setIsSubmitting(true);
        fetch(`${BASE_URL}api/StockTransactions/PostPickup`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Failed to submit data to the backend');
            }
            return response.text(); // Assuming the backend returns JSON
        })
        .then(data => {
            setIsSubmitting(false);
            setConfirmationMessage(`Transaction completed successfully with Document No: ${data}`);
            setReportID(data);
                onConfirm();
                navigate(`/report/${data}`);
        })
        .catch(error => {
            setIsSubmitting(false);
            setError(error.message);
        });
    };
    

    const handleClose = () => {
        onHide();
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    const modalStyle = {
        display: show ? "block" : "none",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        overflow: "auto",
    };

    const modalContentStyle = {
        backgroundColor: "#fefefe",
        margin: "15% auto",
        padding: "20px",
        border: "1px solid #888",
        width: "50%",
        borderRadius: "10px",
    };

    return (
        <div style={modalStyle}>
            <div style={modalContentStyle} className="p-4">
                <span style={{ float: "right", cursor: "pointer" }} onClick={handleClose}>
                    &times;
                </span>
                <h3 className={`modal-title ${title === "Drop Off" ? "drop-off-title" : "pick-up-title"}`}>
                    {title === "Drop Off" ? "📦 Drop Off" : "🚚 Pick Up"}
                </h3>
                {/* New fields for date, incident type, and incident */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <label htmlFor="incidentDate" className="form-label">Date:</label>
                        <DatePicker
                            id="incidentDate"
                            selected={incidentDate}
                            onChange={date => setIncidentDate(date)}
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            isClearable={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="incidentType" className="form-label">Incident Type:</label>
                        <select
                            id="incidentType"
                            className="form-select"
                            value={incidentType}
                            onChange={e => setIncidentType(e.target.value)}
                        >
                            <option value="" disabled>Select Incident Type</option>
                            <option value="Visit">Visit</option>
                            <option value="Telephone Call">Telephone Call</option>
                            <option value="Whatsapp">Whatsapp</option>
                            <option value="Email">Email</option>
                        </select>
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="incident" className="form-label">Incident:</label>
                        <textarea
                            id="incident"
                            className="form-control"
                            rows={2}
                            value={incident}
                            onChange={e => setIncident(e.target.value)}
                        />
                    </div>
                </div>
                <div className="accordion" id="descriptionAccordion">
                    <h3>Select Items</h3>
                    {Object.keys(groupedDescriptions).map((group) => (
                        <div className="accordion-item" key={group}>
                            <h2 className="accordion-header" id={`heading${group}`}>
                                <button
                                    className="accordion-button"
                                    type="button"
                                    onClick={() => toggleGroup(group)}
                                >
                                    <strong>Stocks</strong>
                                </button>
                            </h2>
                            {activeGroup === group && (
                                <div
                                    className="accordion-collapse collapse show"
                                    aria-labelledby={`heading${group}`}
                                >
                                    <div className="accordion-body">
                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Description</th>
                                                    <th>Existing Quantity</th>
                                                    <th>Quantity to Pick Up</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupedDescriptions[group].map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.Description}</td>
                                                        <td>{item.existingQuantity}</td>
                                                        <td>
                                                            <NumericCheckbox
                                                                className="numeric-checkbox"
                                                                value={item.pickUpQuantity}
                                                                onValueChange={(newValue) =>
                                                                    handleValueChange(group, index, newValue)
                                                                }
                                                                max={item.existingQuantity} // Pass the existing quantity as max
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="d-flex justify-content-end mt-4">
                    <button className="btn btn-primary me-2" onClick={handleConfirm} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Processing...
                            </>
                        ) : (
                            "Confirm"
                        )}
                    </button>
                    <button className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </button>
                </div>
                {confirmationMessage && <div className="mt-4 alert alert-success">{confirmationMessage}</div>}
                {error && <div className="mt-4 alert alert-danger">{error}</div>}
            </div>
        </div>
    );
};

export default PickupModal;
